import { useEffect, useRef, useState } from "react";
import { Head, useForm } from "@inertiajs/react";
import { AlertTriangle, CheckCircle2, Download, Save } from "lucide-react";
import SchoolLayout from "@/components/schools/SchoolLayout";
import SegmentedChoice from "@/components/shared/SegmentedChoice";
import { printModeOptions, marginOptions } from "@/lib/printLayout";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Notice } from "@/components/ui/notice";

/**
 * Take-home sheet layout editor. On the left, the real PDF a sheet
 * prints as — rendered by the server for a made-up student with the
 * unsaved choices, at the scale the real students' sheets print at — and
 * on the right, colour or black and white, the margins, and the prize
 * lists it can leave off. Every student's sheet fits on one page: if they
 * run long, they all print slightly smaller.
 */
export default function Edit({
  school, membership, campaign, student_count, print_modes, margins, toggles, settings, update_path, preview_path, download_path, back,
}) {
  const form = useForm({ print_mode: settings.print_mode, margins: settings.margins, hidden: settings.hidden });
  const { data, setData } = form;
  // The scale the current choices print at; null while it's being worked out.
  const [scale, setScale] = useState(null);

  const save = (onSuccess) => form.patch(update_path, { preserveScroll: true, onSuccess });

  // Download what's on screen: save first when there are unsaved changes.
  const download = () => {
    if (form.isDirty) {
      save(() => { window.location.href = download_path; });
    } else {
      window.location.href = download_path;
    }
  };

  const setShown = (key, shown) =>
    setData("hidden", shown ? data.hidden.filter((k) => k !== key) : [...data.hidden, key]);

  const downloadLabel = student_count === 1 ? "Download the sheet" : `Download all ${student_count} sheets`;
  const narrowest = data.margins === margins[margins.length - 1].key;
  const prizesShown = toggles.some((t) => t.count > 0 && !data.hidden.includes(t.key));

  return (
    <SchoolLayout
      school={school}
      membership={membership}
      back={back}
      title="Take-home sheets"
      actions={
        <div className="flex flex-wrap items-center justify-end gap-2">
          {form.isDirty && !form.processing && (
            <span className="text-xs text-muted-foreground mr-1">Unsaved changes</span>
          )}
          <Button variant="outline" size="sm" onClick={() => save()} disabled={!form.isDirty || form.processing}>
            <Save className="h-4 w-4" />
            {form.processing ? "Saving…" : form.recentlySuccessful && !form.isDirty ? "Saved" : "Save changes"}
          </Button>
          {download_path && (
            <Button onClick={download} disabled={form.processing}>
              <Download className="h-4 w-4" />
              {form.isDirty ? `Save & ${downloadLabel.toLowerCase()}` : downloadLabel}
            </Button>
          )}
        </div>
      }
    >
      <Head title={`Take-home sheets · ${campaign.name}`} />

      <div className="grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
        <SheetPreview previewPath={preview_path} printMode={data.print_mode} margins={data.margins} hidden={data.hidden} onScale={setScale} />

        <Card>
          <CardHeader>
            <CardTitle>Layout</CardTitle>
            <CardDescription>
              Each student gets one page. The preview uses a made-up student, Avery S.; every real sheet has the student's own name, classroom, code and QR code.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <SegmentedChoice
              label="Print mode"
              options={printModeOptions(print_modes)}
              value={data.print_mode}
              onChange={(key) => setData("print_mode", key)}
            />
            <SegmentedChoice
              label="Margins"
              options={marginOptions(margins)}
              value={data.margins}
              onChange={(key) => setData("margins", key)}
              note="Narrower margins fit more on the page."
            />

            <div className="space-y-1">
              <p className="mb-1 text-sm font-medium text-foreground">Prizes on the sheet</p>
              {toggles.map((toggle) => (
                <label key={toggle.key} htmlFor={`toggle-${toggle.key}`} className="flex cursor-pointer items-start gap-3 rounded-md px-2 py-1.5 hover:bg-secondary/40">
                  <Checkbox
                    id={`toggle-${toggle.key}`}
                    checked={!data.hidden.includes(toggle.key)}
                    onChange={(e) => setShown(toggle.key, e.target.checked)}
                    className="mt-0.5 h-4 w-4"
                  />
                  <span className="min-w-0">
                    <span className="block text-sm text-foreground">{toggle.label}</span>
                    <span className="block text-xs text-muted-foreground">
                      {toggle.count === 0 ? "None set up yet." : TOGGLE_HINTS[toggle.key]}
                    </span>
                  </span>
                </label>
              ))}
            </div>

            {scale !== null && scale < 1 && (
              <Notice tone="warning">
                Your sheets run a little long, so they all print at {Math.round(scale * 100)}% to fit one page.
                {(!narrowest || prizesShown) && ` ${shrinkTip(!narrowest, prizesShown)}`}
              </Notice>
            )}

            <p className="text-xs text-muted-foreground leading-relaxed">
              Teachers printing their own class get the same layout. Changing it never changes anyone's code, so sheets already handed out keep working.
            </p>
            {!download_path && (
              <p className="text-xs text-muted-foreground">Add students to download their sheets.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </SchoolLayout>
  );
}

const TOGGLE_HINTS = {
  school_prizes: "What the whole school, and each student's own class, earns together.",
  student_prizes: "What each reader earns, plus top-student prizes and draws.",
};

function shrinkTip(canNarrow, canDropPrizes) {
  const ways = [canNarrow && "narrower margins", canDropPrizes && "leaving a prize list off"].filter(Boolean);
  return `For bigger print, try ${ways.join(" or ")}.`;
}

/**
 * The sample sheet as the PDF that prints, re-rendered (debounced) when
 * the options change. The server says what scale the real sheets print
 * at (X-Sheet-Scale); it's reported as null while a render is under way
 * or failed, so the page never shows the last choice's result. Colour
 * doesn't change the scale, so it's remembered per margins and prize
 * lists and sent back (?scale=), sparing the server working it out again.
 */
function SheetPreview({ previewPath, printMode, margins, hidden, onScale }) {
  const [url, setUrl] = useState(null);
  const [scale, setScale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const hiddenKey = hidden.join(",");
  const knownScales = useRef({});

  useEffect(() => {
    const controller = new AbortController();
    let objectUrl = null;
    setLoading(true);
    setFailed(false);
    setScale(null);
    onScale(null);

    // Each preview is a headless-Chrome render on the server, so wait for
    // the clicking to settle.
    const timer = setTimeout(async () => {
      try {
        const layoutKey = `${margins}|${hiddenKey}`;
        const query = new URLSearchParams([["print_mode", printMode], ["margins", margins], ...hidden.map((key) => ["hidden[]", key])]);
        if (knownScales.current[layoutKey]) query.set("scale", knownScales.current[layoutKey]);
        const response = await fetch(`${previewPath}?${query}`, {
          signal: controller.signal,
          headers: { Accept: "application/pdf" },
        });
        if (!response.ok) throw new Error(`Preview failed: ${response.status}`);
        const printedAt = parseFloat(response.headers.get("X-Sheet-Scale")) || 1;
        knownScales.current[layoutKey] = response.headers.get("X-Sheet-Scale");
        objectUrl = URL.createObjectURL(await response.blob());
        setUrl(objectUrl);
        setScale(printedAt);
        onScale(printedAt);
      } catch (error) {
        if (error.name !== "AbortError") setFailed(true);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 400);

    return () => {
      clearTimeout(timer);
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [previewPath, printMode, margins, hiddenKey]);

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex-row items-center justify-between space-y-0 py-3 gap-3">
        <CardTitle className="text-base">Preview</CardTitle>
        <PageStatus loading={loading} failed={failed} scale={scale} />
      </CardHeader>
      <CardContent className="bg-secondary/40 p-4">
        <div className="relative mx-auto w-full max-w-[816px]" style={{ aspectRatio: "8.5 / 11" }}>
          {url && (
            <iframe
              title="Sample take-home sheet"
              src={`${url}#toolbar=0&navpanes=0&view=Fit`}
              className={`absolute inset-0 h-full w-full border-0 bg-white shadow-md transition-opacity ${loading ? "opacity-50" : ""}`}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function PageStatus({ loading, failed, scale }) {
  if (loading) return <span className="text-xs text-muted-foreground">Rendering…</span>;
  if (failed) return <span className="text-xs text-destructive">Couldn't render the preview. Try again in a moment.</span>;
  if (scale < 1) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-warning-foreground">
        <AlertTriangle className="h-3.5 w-3.5" /> Shrunk to {Math.round(scale * 100)}% to fit one page
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-success">
      <CheckCircle2 className="h-3.5 w-3.5" /> Fits on one page
    </span>
  );
}
